import Tooltip from 'antd/es/tooltip';
import cn from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

type ClampedCellProps = {
  children: ReactNode;
  className?: string;
  tooltipTitle?: ReactNode;
  placement?: ComponentProps<typeof Tooltip>['placement'];
};

const baseStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  // This can reduce layout/paint interactions in dense grids:
  contain: 'layout paint',
  WebkitLineClamp: 3
};

function isOverflowing(el: HTMLElement) {
  return (
    el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
  );
}

/**
 * Optimized cell renderer for cells that can overflow after list virtualization
 * In case the cell is overflowing, it adds elipsis + shows a tooltip on hover
 *
 * It breaks the words into max 3 lines of text, before the overflowing occurs
 */

export const ClampedCell = React.memo(function ClampedCell({
  children,
  className,
  tooltipTitle,
  placement
}: ClampedCellProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [showTooltip, setShowTooltip] = useState(false);

  const ensureTooltipIfNeeded = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const truncated = isOverflowing(el);

    if (truncated) {
      if (!showTooltip) setShowTooltip(true);
    } else {
      if (showTooltip) setShowTooltip(false);
    }
  }, [showTooltip]);

  const content = (
    <div
      ref={ref}
      className={cn('break-words', className)}
      style={baseStyle}
      onPointerEnter={ensureTooltipIfNeeded}
    >
      {children}
    </div>
  );

  // Only render Tooltip when needed
  if (!showTooltip) {
    return content;
  }

  return (
    <Tooltip
      title={tooltipTitle ?? children}
      placement={placement}
      mouseEnterDelay={0}
      color="#fff"
      styles={{
        body: {
          color: '#0f172a',
          borderTop: 1,
          borderLeft: 1,
          borderRight: 1,
          borderColor: '#00000033',
          borderStyle: 'solid'
        }
      }}
    >
      {content}
    </Tooltip>
  );
});
